import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { brandSettingsService, DEFAULT_COLORS } from '../../../services/brandSettingsService';
import { useBrand } from '../../../contexts/BrandContext';
import { supabase } from '../../../lib/supabase';

/* ─────────────────────── الثوابت ─────────────────────── */

/** ألوان الموقع العامة — بدون تدرج الهيرو */
const COLOR_FIELDS_GENERAL = [
  { key: 'primary', label: 'اللون الأساسي', desc: 'العنصر الرئيسي في الواجهة', icon: 'Palette' },
  { key: 'secondary', label: 'اللون الثانوي', desc: 'التأكيد والتمييز', icon: 'Sparkles' },
  { key: 'text', label: 'لون النص', desc: 'النصوص والعناوين', icon: 'Type' },
  { key: 'background', label: 'الخلفية العامة', desc: 'خلفية الصفحات', icon: 'Square' },
  { key: 'button', label: 'لون الأزرار', desc: 'أزرار الإجراءات الرئيسية', icon: 'MousePointerClick' },
  { key: 'link', label: 'لون الروابط', desc: 'الروابط القابلة للنقر', icon: 'Link' },
  { key: 'hover', label: 'لون الهوفر', desc: 'عند المرور فوق الأزرار', icon: 'Hand' },
  { key: 'accent', label: 'لون التمييز', desc: 'قوائم منسدلة وعناصر ثانوية', icon: 'Highlighter' },
  { key: 'menuIcon', label: 'أيقونات القائمة', desc: 'القائمة الرئيسية والجانبية', icon: 'LayoutList' },
  { key: 'footer', label: 'لون الفوتر', desc: 'خلفية تذييل الصفحة', icon: 'Layout' },
];

/** لونَا تدرج زر الهيرو فقط — قسمان منفصلان حتى يظهرا بوضوح */
const COLOR_FIELDS_HERO_CTA = [
  { key: 'heroCtaGradientStart', label: 'بداية التدرج', desc: 'الجهة اليمنى من الزر (RTL)', icon: 'Circle' },
  { key: 'heroCtaGradientEnd', label: 'نهاية التدرج', desc: 'الجهة اليسرى من الزر (RTL)', icon: 'CircleDot' },
];

/* ─────────────── المكون الرئيسي ─────────────── */

export default function BrandSettingsTab() {
  const { refreshBrand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  /* ─── الحالة المحفوظة ─── */
  const [saved, setSaved] = useState({
    headerLogoUrl: null,
    footerLogoUrl: null,
    faviconUrl: null,
    bannerUrl: null,
    mobileBannerUrl: null,
    headerLogoScale: 100,
    footerLogoScale: 100,
    colors: { ...DEFAULT_COLORS },
  });

  /* ─── حالة التعديلات المحلية ─── */
  const [previews, setPreviews] = useState({ headerLogo: null, footerLogo: null, favicon: null, banner: null, mobileBanner: null });
  const [files, setFiles] = useState({ headerLogo: null, footerLogo: null, favicon: null, banner: null, mobileBanner: null });
  // تتبع الصور التي طلب المشرف حذفها (القيم القديمة الموجودة)
  const [removedUrls, setRemovedUrls] = useState({ headerLogo: false, footerLogo: false, favicon: false, banner: false, mobileBanner: false });
  const [scales, setScales] = useState({ header: 100, footer: 100 });
  const [colors, setColors] = useState({ ...DEFAULT_COLORS });

  /* ─── حالة Analytics ─── */
  const [gaId, setGaId]           = useState('');
  const [savedGaId, setSavedGaId] = useState('');
  const [savingGa, setSavingGa]   = useState(false);
  const [gaMsg, setGaMsg]         = useState({ type: '', text: '' });

  /* ─── حالة Snapchat Pixel ─── */
  const [snapId, setSnapId]           = useState('');
  const [savedSnapId, setSavedSnapId] = useState('');
  const [savingSnap, setSavingSnap]   = useState(false);
  const [snapMsg, setSnapMsg]         = useState({ type: '', text: '' });

  /* ─── مراجع ─── */
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const faviconRef = useRef(null);
  const bannerRef = useRef(null);
  const mobileBannerRef = useRef(null);

  /* ─── تتبع التغييرات ─── */
  const hasRemovals = Object.values(removedUrls).some(Boolean);
  const hasLogoChanges = files.headerLogo || files.footerLogo || files.favicon || files.banner || files.mobileBanner || hasRemovals;
  const hasScaleChanges = scales.header !== saved.headerLogoScale || scales.footer !== saved.footerLogoScale;
  const hasColorChanges = Object.keys(DEFAULT_COLORS).some(k => colors[k] !== saved.colors[k]);
  const hasChanges = hasLogoChanges || hasScaleChanges || hasColorChanges;

  /* ─── تحميل البيانات ─── */
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [brandData, gaData, snapData] = await Promise.all([
        brandSettingsService.getBrandSettings(),
        supabase.from('app_settings').select('value').eq('key', 'google_analytics_id').maybeSingle(),
        supabase.from('app_settings').select('value').eq('key', 'snapchat_pixel_id').maybeSingle(),
      ]);
      setSaved(brandData);
      setScales({ header: brandData.headerLogoScale, footer: brandData.footerLogoScale });
      setColors({ ...DEFAULT_COLORS, ...brandData.colors });
      setRemovedUrls({ headerLogo: false, footerLogo: false, favicon: false, banner: false, mobileBanner: false });
      const snapVal = snapData?.data?.value || '';
      setSnapId(snapVal);
      setSavedSnapId(snapVal);
      const id = gaData?.data?.value || '';
      setGaId(id);
      setSavedGaId(id);
    } catch (err) {
      console.error('❌ Load settings error:', err);
      setMessage({ type: 'error', text: `فشل تحميل الإعدادات: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  /* ─── حفظ GA ID ─── */
  const handleSaveGa = async () => {
    const trimmed = gaId.trim();
    // التحقق من صيغة GA4: G- متبوعاً بأحرف وأرقام
    if (trimmed && !/^G-[A-Z0-9]{4,}$/i.test(trimmed)) {
      setGaMsg({ type: 'error', text: 'صيغة المعرّف غير صحيحة. يجب أن تكون بالشكل: G-XXXXXXXXXX' });
      return;
    }
    try {
      setSavingGa(true);
      setGaMsg({ type: '', text: '' });
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key: 'google_analytics_id', value: trimmed }, { onConflict: 'key', ignoreDuplicates: false });
      if (error) throw new Error(error.message);
      setSavedGaId(trimmed);
      setGaMsg({ type: 'success', text: trimmed ? 'تم حفظ معرّف Google Analytics بنجاح ✅' : 'تم حذف معرّف Google Analytics' });
    } catch (err) {
      setGaMsg({ type: 'error', text: `فشل الحفظ: ${err.message}` });
    } finally {
      setSavingGa(false);
    }
  };

  /* ─── حفظ Snapchat Pixel ID ─── */
  const handleSaveSnap = async () => {
    const trimmed = snapId.trim();
    try {
      setSavingSnap(true);
      setSnapMsg({ type: '', text: '' });
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key: 'snapchat_pixel_id', value: trimmed }, { onConflict: 'key', ignoreDuplicates: false });
      if (error) throw new Error(error.message);
      setSavedSnapId(trimmed);
      setSnapMsg({ type: 'success', text: trimmed ? 'تم حفظ Snapchat Pixel ID بنجاح ✅' : 'تم حذف Snapchat Pixel ID' });
    } catch (err) {
      setSnapMsg({ type: 'error', text: `فشل الحفظ: ${err.message}` });
    } finally {
      setSavingSnap(false);
    }
  };

  /* ─── اختيار ملف ─── */
  const handleFileSelect = useCallback((type, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isBanner = type === 'banner' || type === 'mobileBanner';
    const maxSize = isBanner ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setMessage({ type: 'error', text: `حجم الملف يجب أن يكون أقل من ${isBanner ? '10' : '5'} ميجابايت` });
      return;
    }

    const validLogo = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    const validFav = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/jpeg', 'image/svg+xml'];
    const validBanner = ['image/png', 'image/jpeg', 'image/webp'];
    const valid = type === 'favicon' ? validFav : isBanner ? validBanner : validLogo;

    if (!valid.includes(file.type) && !file.name.endsWith('.ico')) {
      setMessage({ type: 'error', text: 'نوع الملف غير مدعوم' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setPreviews(p => ({ ...p, [type]: ev.target.result }));
    reader.readAsDataURL(file);
    setFiles(p => ({ ...p, [type]: file }));
    // إلغاء طلب الإزالة إذا اختار ملفاً جديداً
    setRemovedUrls(p => ({ ...p, [type]: false }));
    setMessage({ type: '', text: '' });
  }, []);

  const handleRemoveFile = useCallback((type) => {
    setFiles(p => ({ ...p, [type]: null }));
    setPreviews(p => ({ ...p, [type]: null }));
    // إذا كانت هناك صورة محفوظة مسبقاً → سجّل طلب الحذف
    setRemovedUrls(p => ({ ...p, [type]: true }));
    const refs = { headerLogo: headerRef, footerLogo: footerRef, favicon: faviconRef, banner: bannerRef, mobileBanner: mobileBannerRef };
    if (refs[type]?.current) refs[type].current.value = '';
  }, []);

  /* ─── حفظ ─── */
  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // ابدأ بالقيم الحالية المحفوظة
      const updated = {
        headerLogoUrl: removedUrls.headerLogo ? null : saved.headerLogoUrl,
        footerLogoUrl: removedUrls.footerLogo ? null : saved.footerLogoUrl,
        faviconUrl: removedUrls.favicon ? null : saved.faviconUrl,
        bannerUrl: removedUrls.banner ? null : saved.bannerUrl,
        mobileBannerUrl: removedUrls.mobileBanner ? null : saved.mobileBannerUrl,
        headerLogoScale: scales.header,
        footerLogoScale: scales.footer,
        colors: { ...colors },
      };

      // حذف الصور القديمة من storage عند الإزالة
      if (removedUrls.headerLogo && saved.headerLogoUrl) await brandSettingsService.deleteOldAsset(saved.headerLogoUrl);
      if (removedUrls.footerLogo && saved.footerLogoUrl) await brandSettingsService.deleteOldAsset(saved.footerLogoUrl);
      if (removedUrls.favicon && saved.faviconUrl) await brandSettingsService.deleteOldAsset(saved.faviconUrl);
      if (removedUrls.banner && saved.bannerUrl) await brandSettingsService.deleteOldAsset(saved.bannerUrl);
      if (removedUrls.mobileBanner && saved.mobileBannerUrl) await brandSettingsService.deleteOldAsset(saved.mobileBannerUrl);

      // رفع الملفات الجديدة
      if (files.headerLogo) {
        if (saved.headerLogoUrl) await brandSettingsService.deleteOldAsset(saved.headerLogoUrl);
        updated.headerLogoUrl = await brandSettingsService.uploadBrandAsset(files.headerLogo, 'header-logo');
      }
      if (files.footerLogo) {
        if (saved.footerLogoUrl) await brandSettingsService.deleteOldAsset(saved.footerLogoUrl);
        updated.footerLogoUrl = await brandSettingsService.uploadBrandAsset(files.footerLogo, 'footer-logo');
      }
      if (files.favicon) {
        if (saved.faviconUrl) await brandSettingsService.deleteOldAsset(saved.faviconUrl);
        updated.faviconUrl = await brandSettingsService.uploadBrandAsset(files.favicon, 'favicon');
      }
      if (files.banner) {
        if (saved.bannerUrl) await brandSettingsService.deleteOldAsset(saved.bannerUrl);
        updated.bannerUrl = await brandSettingsService.uploadBrandAsset(files.banner, 'banner');
      }
      if (files.mobileBanner) {
        if (saved.mobileBannerUrl) await brandSettingsService.deleteOldAsset(saved.mobileBannerUrl);
        updated.mobileBannerUrl = await brandSettingsService.uploadBrandAsset(files.mobileBanner, 'mobile-banner');
      }

      await brandSettingsService.saveAllBrandSettings(updated);

      setSaved(updated);
      setFiles({ headerLogo: null, footerLogo: null, favicon: null, banner: null, mobileBanner: null });
      setPreviews({ headerLogo: null, footerLogo: null, favicon: null, banner: null, mobileBanner: null });
      setRemovedUrls({ headerLogo: false, footerLogo: false, favicon: false, banner: false, mobileBanner: false });

      await refreshBrand();
      setMessage({ type: 'success', text: '✅ تم حفظ جميع إعدادات الهوية البصرية بنجاح' });
    } catch (err) {
      console.error('❌ Save error:', err);
      setMessage({ type: 'error', text: `فشل الحفظ: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  /* ─── إعادة تعيين الألوان ─── */
  const resetColors = () => {
    setColors({ ...DEFAULT_COLORS });
  };

  /* ─── تحميل ─── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            جاري تحميل إعدادات الهوية البصرية...
          </p>
        </div>
      </div>
    );
  }

  /* ═══════════════════ العرض ═══════════════════ */
  return (
    <div className="space-y-8">

      {/* ─── الرأس + زر الحفظ ─── */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Icon name="Palette" size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                إعدادات الهوية البصرية
              </h2>
              <p className="text-sm text-slate-600">إدارة الشعارات والبانر والأحجام وألوان الموقع</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
              hasChanges ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                جاري الحفظ...
              </>
            ) : (
              <>
                <Icon name="Save" size={18} />
                حفظ جميع التغييرات
              </>
            )}
          </button>
        </div>

        {message.text && (
          <div className={`mt-4 p-3 rounded-xl text-sm font-medium ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}
      </div>

      {/* ══════════════ القسم 1: الشعارات ══════════════ */}
      <SectionHeader icon="Image" title="الشعارات والأيقونات" desc="رفع وتعديل شعارات الموقع وأيقونة المتصفح" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* شعار الهيدر */}
        <LogoCard
          title="شعار الهيدر"
          description="يظهر في الهيدر وصفحات تسجيل الدخول والقائمة الجانبية. لا يؤثر على الفوتر."
          icon="LayoutDashboard"
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
          currentUrl={saved.headerLogoUrl}
          previewUrl={previews.headerLogo}
          isRemoved={removedUrls.headerLogo}
          inputRef={headerRef}
          accept=".png,.jpg,.jpeg,.svg,.webp"
          onFileSelect={(e) => handleFileSelect('headerLogo', e)}
          onRemove={() => handleRemoveFile('headerLogo')}
          hasNewFile={!!files.headerLogo}
          fileName={files.headerLogo?.name}
          scale={scales.header}
          onScaleChange={(v) => setScales(s => ({ ...s, header: v }))}
          bgColor="#f8fafc"
        />

        {/* شعار الفوتر */}
        <LogoCard
          title="شعار الفوتر"
          description="حقل مستقل تماماً عن شعار الهيدر. يُستخدم في الفوتر فقط."
          icon="PanelBottom"
          iconColor="text-indigo-600"
          iconBg="bg-indigo-100"
          currentUrl={saved.footerLogoUrl}
          previewUrl={previews.footerLogo}
          isRemoved={removedUrls.footerLogo}
          inputRef={footerRef}
          accept=".png,.jpg,.jpeg,.svg,.webp"
          onFileSelect={(e) => handleFileSelect('footerLogo', e)}
          onRemove={() => handleRemoveFile('footerLogo')}
          hasNewFile={!!files.footerLogo}
          fileName={files.footerLogo?.name}
          scale={scales.footer}
          onScaleChange={(v) => setScales(s => ({ ...s, footer: v }))}
          bgColor="#1e293b"
        />
      </div>

      {/* فافيكون */}
      <div className="max-w-md">
        <FaviconCard
          currentUrl={saved.faviconUrl}
          previewUrl={previews.favicon}
          isRemoved={removedUrls.favicon}
          inputRef={faviconRef}
          accept=".png,.ico,.jpg,.jpeg,.svg"
          onFileSelect={(e) => handleFileSelect('favicon', e)}
          onRemove={() => handleRemoveFile('favicon')}
          hasNewFile={!!files.favicon}
          fileName={files.favicon?.name}
        />
      </div>

      {/* ══════════════ القسم 2: بانر الصفحة الرئيسية ══════════════ */}
      <SectionHeader icon="Monitor" title="بانر الصفحة الرئيسية" desc="صورتان منفصلتان — سطح المكتب للشاشات الكبيرة، الجوال للشاشات الصغيرة" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <BannerCard
          title="بانر سطح المكتب"
          desc="يظهر على الشاشات الكبيرة. المقاس المثالي: 1920 × 800 بكسل (نسبة 2.4:1)"
          icon="Monitor"
          iconBg="bg-orange-100"
          iconColor="text-orange-600"
          currentUrl={saved.bannerUrl}
          previewUrl={previews.banner}
          isRemoved={removedUrls.banner}
          inputRef={bannerRef}
          onFileSelect={(e) => handleFileSelect('banner', e)}
          onRemove={() => handleRemoveFile('banner')}
          hasNewFile={!!files.banner}
          fileName={files.banner?.name}
          aspectHint="1920 × 800"
        />

        <BannerCard
          title="بانر الجوال"
          desc="يظهر على شاشات الجوال فقط. المقاس المثالي: 768 × 900 بكسل (نسبة 0.85:1)"
          icon="Smartphone"
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
          currentUrl={saved.mobileBannerUrl}
          previewUrl={previews.mobileBanner}
          isRemoved={removedUrls.mobileBanner}
          inputRef={mobileBannerRef}
          onFileSelect={(e) => handleFileSelect('mobileBanner', e)}
          onRemove={() => handleRemoveFile('mobileBanner')}
          hasNewFile={!!files.mobileBanner}
          fileName={files.mobileBanner?.name}
          aspectHint="768 × 900"
          isMobile
        />
      </div>

      {/* ══════════════ القسم 3: الألوان ══════════════ */}
      <SectionHeader icon="Paintbrush" title="إعدادات ألوان الموقع" desc="تغيير ألوان الموقع بالكامل من هنا — التغييرات تنعكس مباشرة بعد الحفظ" />

      {/* معاينة الألوان */}
      <ColorLivePreview colors={colors} />

      {/* حقول الألوان */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            اختيار الألوان
          </h3>
          <button
            onClick={resetColors}
            className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors flex items-center gap-1"
          >
            <Icon name="RotateCcw" size={14} />
            إعادة الألوان الافتراضية
          </button>
        </div>
        <div className="p-6 space-y-8">
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-3" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              ألوان الواجهة
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {COLOR_FIELDS_GENERAL.map(f => (
                <ColorField
                  key={f.key}
                  label={f.label}
                  desc={f.desc}
                  icon={f.icon}
                  value={colors[f.key]}
                  onChange={(v) => setColors(c => ({ ...c, [f.key]: v }))}
                />
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              زر «ابدأ الحجز الآن» في الصفحة الرئيسية
            </p>
            <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
              لونان يحدّدان التدرج من اليمين إلى اليسار. يظهران بعد حفظ الإعدادات وتحديث الصفحة.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
              {COLOR_FIELDS_HERO_CTA.map(f => (
                <ColorField
                  key={f.key}
                  label={f.label}
                  desc={f.desc}
                  icon={f.icon}
                  value={colors[f.key]}
                  onChange={(v) => setColors(c => ({ ...c, [f.key]: v }))}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────── قسم Google Analytics ─────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
            <Icon name="BarChart2" size={16} className="text-orange-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">التحليلات والتتبع</h3>
            <p className="text-xs text-slate-500">Google Analytics 4</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              معرّف القياس (Measurement ID)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={gaId}
                onChange={e => { setGaId(e.target.value); setGaMsg({ type: '', text: '' }); }}
                placeholder="G-XXXXXXXXXX"
                dir="ltr"
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 font-mono"
              />
              <button
                onClick={handleSaveGa}
                disabled={savingGa || gaId.trim() === savedGaId}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
              >
                {savingGa ? 'جاري الحفظ...' : 'حفظ'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              أدخل معرّف القياس من{' '}
              <a
                href="https://analytics.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 underline"
              >
                Google Analytics
              </a>
              {' '}→ الإدارة → تدفق البيانات. مثال: <span className="font-mono">G-ABC123XYZ</span>
            </p>
          </div>

          {gaMsg.text && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              gaMsg.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
              <Icon name={gaMsg.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={14} />
              {gaMsg.text}
            </div>
          )}

          {savedGaId && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-700">
                مُفعَّل حالياً: <span className="font-mono font-semibold">{savedGaId}</span>
              </span>
            </div>
          )}

          <div className="text-xs text-slate-400 space-y-1 pt-1 border-t border-slate-100">
            <p className="flex items-center gap-1.5">
              <Icon name="Info" size={11} className="text-slate-400 shrink-0" />
              يُطبَّق على الموقع الإلكتروني فوراً عند إعادة تحميل الصفحة.
            </p>
            <p className="flex items-center gap-1.5">
              <Icon name="Info" size={11} className="text-slate-400 shrink-0" />
              لإيقاف التتبع، امسح الحقل واحفظ.
            </p>
          </div>

          {/* ─── Snapchat Pixel ─── */}
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-yellow-50 flex items-center justify-center">
                <Icon name="Zap" size={13} className="text-yellow-500" />
              </div>
              <span className="text-xs font-semibold text-slate-700">Snapchat Pixel</span>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Pixel ID
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={snapId}
                  onChange={e => { setSnapId(e.target.value); setSnapMsg({ type: '', text: '' }); }}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  dir="ltr"
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-300 font-mono"
                />
                <button
                  onClick={handleSaveSnap}
                  disabled={savingSnap || snapId.trim() === savedSnapId}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-yellow-400 text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-yellow-500 transition-colors"
                >
                  {savingSnap ? 'جاري الحفظ...' : 'حفظ'}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                احصل على الـ Pixel ID من{' '}
                <a href="https://ads.snapchat.com" target="_blank" rel="noopener noreferrer" className="text-yellow-600 underline">
                  Snapchat Ads Manager
                </a>
                {' '}← Events Manager ← Pixel. مثال: <span className="font-mono">a1b2c3d4-xxxx-xxxx-xxxx-xxxxxxxxxxxx</span>
              </p>
            </div>
            {snapMsg.text && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                snapMsg.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-600 border border-red-200'
              }`}>
                <Icon name={snapMsg.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={14} />
                {snapMsg.text}
              </div>
            )}
            {savedSnapId && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                <span className="text-xs text-yellow-800">
                  مُفعَّل حالياً: <span className="font-mono font-semibold">{savedSnapId}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ملاحظات */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800 space-y-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            <p className="font-bold">ملاحظات مهمة:</p>
            <ul className="list-disc list-inside space-y-1 mr-2">
              <li>الأنواع المدعومة للشعارات: PNG, JPG, SVG, WebP (حتى 5 ميجابايت)</li>
              <li>البانر يدعم: PNG, JPG, WebP (حتى 10 ميجابايت) — المقاس المثالي: <strong>1920 × 800 بكسل</strong></li>
              <li>شعار الهيدر وشعار الفوتر مستقلان تماماً عن بعضهما</li>
              <li>يمكنك تكبير أو تصغير الشعار (50% – 150%) مع الحفاظ على نسبة الأبعاد</li>
              <li>ألوان الموقع تُطبَّق ديناميكياً على جميع الصفحات بعد الحفظ</li>
              <li>يمكنك إعادة الألوان الافتراضية في أي وقت بضغطة زر</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   المكونات الفرعية
   ═══════════════════════════════════════════════════════════════ */

/** عنوان قسم */
function SectionHeader({ icon, title, desc }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <Icon name={icon} size={20} className="text-slate-600" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          {title}
        </h3>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
    </div>
  );
}


/** بطاقة شعار (هيدر أو فوتر) مع معاينة كبيرة + سلايدر */
function LogoCard({
  title, description, icon, iconColor, iconBg,
  currentUrl, previewUrl, isRemoved, inputRef,
  accept, onFileSelect, onRemove,
  hasNewFile, fileName,
  scale, onScaleChange, bgColor,
}) {
  // عند الحذف: لا تعرض الصورة القديمة؛ عرض الملف الجديد إذا وُجد
  const displayUrl = isRemoved ? (previewUrl || null) : (previewUrl || currentUrl);
  const scaleRatio = scale / 100;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon name={icon} size={20} className={iconColor} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            {title}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* معاينة كبيرة */}
        <div
          className="rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors"
          style={{
            backgroundColor: bgColor,
            minHeight: 140,
            maxHeight: 200,
            borderColor: displayUrl ? '#93c5fd' : '#e2e8f0',
          }}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={title}
              style={{
                maxWidth: '80%',
                maxHeight: 160,
                transform: `scale(${scaleRatio})`,
                transformOrigin: 'center',
                transition: 'transform 0.2s ease',
              }}
              className="object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="text-center py-8">
              <Icon name="ImagePlus" size={36} className="text-slate-300 mx-auto" />
              <p className="text-xs text-slate-400 mt-2">لا يوجد شعار — ارفع صورة</p>
            </div>
          )}
        </div>

        {/* سلايدر */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Icon name="ZoomIn" size={14} />
              حجم الشعار
            </span>
            <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">{scale}%</span>
          </div>
          <input
            type="range"
            min={50}
            max={150}
            step={5}
            value={scale}
            onChange={(e) => onScaleChange(Number(e.target.value))}
            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-[10px] text-slate-400">
            <span>50%</span>
            <span>100%</span>
            <span>150%</span>
          </div>
        </div>

        {/* حالة الملف */}
        {/* ملف جديد مختار */}
        {hasNewFile && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Icon name="FileImage" size={14} className="text-blue-600 flex-shrink-0" />
            <span className="text-xs text-blue-700 truncate flex-1" dir="ltr">{fileName}</span>
            <button onClick={onRemove} className="text-blue-400 hover:text-red-500 flex-shrink-0" title="إلغاء">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* محذوف — في انتظار الحفظ */}
        {!hasNewFile && isRemoved && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <Icon name="Trash2" size={14} className="text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600 flex-1">سيُحذف الشعار عند الحفظ</span>
          </div>
        )}

        {/* محفوظ ومفعّل */}
        {!hasNewFile && !isRemoved && currentUrl && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <Icon name="CheckCircle" size={14} className="text-green-600 flex-shrink-0" />
            <span className="text-xs text-green-700 flex-1">شعار محفوظ ومفعّل</span>
            <button
              onClick={onRemove}
              className="text-slate-300 hover:text-red-500 flex-shrink-0 transition-colors"
              title="حذف الشعار"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        )}

        {/* زر الرفع */}
        <input ref={inputRef} type="file" accept={accept} onChange={onFileSelect} className="hidden" />
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 text-slate-600 hover:text-blue-600 transition-all flex items-center justify-center gap-2 text-sm font-medium"
        >
          <Icon name="Upload" size={16} />
          {hasNewFile ? 'تغيير الملف' : (currentUrl && !isRemoved) ? 'تغيير الشعار' : 'رفع شعار'}
        </button>
      </div>
    </div>
  );
}


/** بطاقة الفافيكون */
function FaviconCard({ currentUrl, previewUrl, isRemoved, inputRef, accept, onFileSelect, onRemove, hasNewFile, fileName }) {
  const displayUrl = isRemoved ? (previewUrl || null) : (previewUrl || currentUrl);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Icon name="Globe" size={20} className="text-emerald-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            أيقونة الموقع (Favicon)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">تظهر في شريط العناوين وتبويبات المتصفح</p>
        </div>
      </div>

      <div className="p-5 flex items-center gap-5">
        <div className="w-20 h-20 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden flex-shrink-0"
          style={{ borderColor: displayUrl ? '#6ee7b7' : '#e2e8f0', backgroundColor: displayUrl ? '#f0fdf4' : '#f8fafc' }}
        >
          {displayUrl ? (
            <img src={displayUrl} alt="Favicon" className="w-12 h-12 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <Icon name="ImagePlus" size={24} className="text-slate-300" />
          )}
        </div>

        <div className="flex-1 space-y-3">
          {hasNewFile && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
              <Icon name="FileImage" size={14} className="text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-700 truncate flex-1" dir="ltr">{fileName}</span>
              <button onClick={onRemove} className="text-blue-400 hover:text-red-500 flex-shrink-0" title="إلغاء">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}
          {!hasNewFile && isRemoved && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
              <Icon name="Trash2" size={14} className="text-red-500 flex-shrink-0" />
              <span className="text-xs text-red-600 flex-1">ستُحذف عند الحفظ</span>
            </div>
          )}
          {!hasNewFile && !isRemoved && currentUrl && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <Icon name="CheckCircle" size={14} className="text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700 flex-1">أيقونة محفوظة</span>
              <button
                onClick={onRemove}
                className="text-slate-300 hover:text-red-500 flex-shrink-0 transition-colors"
                title="حذف الأيقونة"
              >
                <Icon name="Trash2" size={14} />
              </button>
            </div>
          )}

          <input ref={inputRef} type="file" accept={accept} onChange={onFileSelect} className="hidden" />
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 text-slate-600 hover:text-emerald-600 transition-all flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Icon name="Upload" size={16} />
            {hasNewFile ? 'تغيير الملف' : (currentUrl && !isRemoved) ? 'تغيير الأيقونة' : 'رفع أيقونة'}
          </button>
        </div>
      </div>
    </div>
  );
}


/** بطاقة البانر (سطح المكتب أو الجوال) */
function BannerCard({
  title, desc, icon = 'Monitor', iconBg = 'bg-orange-100', iconColor = 'text-orange-600',
  currentUrl, previewUrl, isRemoved, inputRef, onFileSelect, onRemove,
  hasNewFile, fileName, aspectHint = '1920 × 800', isMobile = false,
}) {
  const displayUrl = isRemoved ? (previewUrl || null) : (previewUrl || currentUrl);
  const aspectRatio = isMobile ? '768 / 900' : '1920 / 800';
  const maxH = isMobile ? 320 : 240;
  const accentBorder = isMobile ? '#c4b5fd' : '#fdba74';
  const hoverBorder = isMobile ? 'hover:border-purple-400 hover:bg-purple-50/50 hover:text-purple-600' : 'hover:border-orange-400 hover:bg-orange-50/50 hover:text-orange-600';
  const infoBg = isMobile ? 'bg-purple-50/50 border-purple-100' : 'bg-orange-50/50 border-orange-100';
  const infoText = isMobile ? 'text-purple-700' : 'text-orange-700';
  const infoIcon = isMobile ? 'text-purple-500' : 'text-orange-500';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon name={icon} size={20} className={iconColor} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            {title}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* معاينة البانر */}
        <div
          className="rounded-xl border-2 border-dashed overflow-hidden transition-colors"
          style={{
            borderColor: displayUrl ? accentBorder : '#e2e8f0',
            backgroundColor: displayUrl ? '#1e293b' : '#f8fafc',
            aspectRatio,
            maxHeight: maxH,
          }}
        >
          {displayUrl ? (
            <div className="relative w-full h-full">
              <img
                src={displayUrl}
                alt={title}
                className="w-full h-full object-cover"
                style={{ maxHeight: maxH }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-transparent to-slate-900/30 pointer-events-none" />
              <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded-lg">
                معاينة
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-8">
              <Icon name="ImagePlus" size={36} className="text-slate-300 mb-2" />
              <p className="text-xs text-slate-400">لا توجد صورة — ارفع صورة</p>
              <p className="text-[10px] text-slate-400 mt-1">{aspectHint} بكسل</p>
            </div>
          )}
        </div>

        {/* حالة الملف */}
        {hasNewFile && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Icon name="FileImage" size={14} className="text-blue-600 flex-shrink-0" />
            <span className="text-xs text-blue-700 truncate flex-1" dir="ltr">{fileName}</span>
            <button onClick={onRemove} className="text-blue-400 hover:text-red-500 flex-shrink-0" title="إلغاء">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {!hasNewFile && isRemoved && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <Icon name="Trash2" size={14} className="text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600 flex-1">سيُحذف البانر عند الحفظ</span>
          </div>
        )}

        {!hasNewFile && !isRemoved && currentUrl && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <Icon name="CheckCircle" size={14} className="text-green-600 flex-shrink-0" />
            <span className="text-xs text-green-700 flex-1">بانر محفوظ ومفعّل</span>
            <button onClick={onRemove} className="text-slate-300 hover:text-red-500 flex-shrink-0 transition-colors" title="حذف البانر">
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        )}

        <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={onFileSelect} className="hidden" />
        <button
          onClick={() => inputRef.current?.click()}
          className={`w-full px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 ${hoverBorder} text-slate-600 transition-all flex items-center justify-center gap-2 text-sm font-medium`}
        >
          <Icon name="Upload" size={16} />
          {hasNewFile ? 'تغيير الملف' : (currentUrl && !isRemoved) ? 'تغيير البانر' : 'رفع صورة البانر'}
        </button>

        {/* معلومات سريعة */}
        <div className={`${infoBg} border rounded-lg p-3`}>
          <div className="flex items-start gap-2">
            <Icon name="Info" size={14} className={`${infoIcon} mt-0.5 flex-shrink-0`} />
            <div className={`text-[11px] ${infoText} space-y-0.5`}>
              <p>• <strong>المقاس المثالي:</strong> {aspectHint} بكسل</p>
              <p>• <strong>الأنواع المدعومة:</strong> PNG, JPG, WebP</p>
              <p>• <strong>الحد الأقصى:</strong> 10 ميجابايت</p>
              <p>• يُفضل صورة بجودة عالية للحصول على أفضل نتيجة</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/** تحويل hex مختصر 3 أحرف إلى 6 أحرف */
function expandHex(hex) {
  const h = hex.replace('#', '');
  if (h.length === 3) return '#' + h.split('').map(c => c + c).join('');
  if (h.length === 6) return '#' + h;
  return null;
}

/** تحقق من صحة hex (3 أو 6 أحرف) */
function isValidHex(hex) {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
}

/** حقل لون واحد مع دعم الهيكس */
function ColorField({ label, desc, icon, value, onChange }) {
  const inputRef = useRef(null);
  const [rawText, setRawText] = useState(value || '#000000');
  const [isInvalid, setIsInvalid] = useState(false);

  // مزامنة النص مع القيمة الخارجية (عند التغيير من الـ color picker)
  useEffect(() => {
    setRawText(value || '#000000');
    setIsInvalid(false);
  }, [value]);

  const handleTextChange = (e) => {
    let v = e.target.value.trim();
    // أضف # تلقائياً إن لم يكن موجوداً
    if (v && !v.startsWith('#')) v = '#' + v;
    // السماح بالكتابة الحرة في الحقل
    setRawText(v);

    const expanded = expandHex(v);
    if (expanded) {
      setIsInvalid(false);
      onChange(expanded);
    } else {
      setIsInvalid(v.length > 1); // خطأ فقط إن كان هناك محتوى
    }
  };

  const handleBlur = () => {
    // عند مغادرة الحقل: إن كان الـ hex غير مكتمل، ارجع للقيمة المحفوظة
    if (!isValidHex(rawText) && !isValidHex(expandHex(rawText) || '')) {
      setRawText(value || '#000000');
      setIsInvalid(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text').trim();
    if (!pasted.startsWith('#')) pasted = '#' + pasted;
    const expanded = expandHex(pasted);
    if (expanded) {
      setRawText(expanded);
      setIsInvalid(false);
      onChange(expanded);
    } else {
      setRawText(pasted);
      setIsInvalid(true);
    }
  };

  // لون المعاينة: استخدم القيمة الموسّعة إن أمكن وإلا القيمة المحفوظة
  const previewColor = expandHex(rawText) || value || '#000000';

  return (
    <div className={`min-w-0 bg-slate-50 rounded-xl border p-3 sm:p-4 hover:border-blue-200 transition-colors group ${isInvalid ? 'border-red-300' : 'border-slate-100'}`}>
      <div className="flex gap-2 items-start mb-2.5">
        <div className="flex-shrink-0 pt-0.5">
          <Icon name={icon} size={16} className="text-slate-500 group-hover:text-blue-500 transition-colors" />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm font-bold text-slate-800 leading-snug" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>{label}</p>
          <p className="text-[10px] text-slate-400 leading-snug line-clamp-2">{desc}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        {/* مربع معاينة اللون — يفتح الـ color picker عند الضغط */}
        <button
          onClick={() => inputRef.current?.click()}
          className="w-10 h-10 rounded-lg border-2 border-slate-200 hover:border-blue-400 cursor-pointer transition-colors flex-shrink-0 shadow-sm"
          style={{ backgroundColor: previewColor }}
          title="اضغط لفتح منتقي الألوان"
        />
        {/* color picker مخفي */}
        <input
          ref={inputRef}
          type="color"
          value={value?.length === 7 ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
        {/* حقل الـ hex */}
        <input
          type="text"
          value={rawText}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onPaste={handlePaste}
          dir="ltr"
          maxLength={7}
          spellCheck={false}
          className={`min-w-0 flex-1 max-w-full px-2.5 py-2 rounded-lg border text-xs font-mono bg-white outline-none transition-all
            ${isInvalid
              ? 'border-red-300 text-red-600 focus:ring-2 focus:ring-red-100'
              : 'border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400'
            }`}
          placeholder="#RRGGBB"
        />
        {/* مؤشر الحالة */}
        {isInvalid ? (
          <Icon name="X" size={16} className="text-red-400 flex-shrink-0" />
        ) : isValidHex(rawText) ? (
          <Icon name="Check" size={16} className="text-green-500 flex-shrink-0" />
        ) : null}
      </div>
      {isInvalid && (
        <p className="text-[10px] text-red-400 mt-1.5 mr-12">
          أدخل هيكس صحيح مثل #FF5733 أو #F53
        </p>
      )}
    </div>
  );
}


/** معاينة مباشرة للألوان */
function ColorLivePreview({ colors }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Icon name="Eye" size={18} className="text-slate-500" />
          <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            معاينة مباشرة للألوان
          </h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Live Preview</span>
        </div>
      </div>

      <div className="p-6" style={{ backgroundColor: colors.background }}>
        <div className="rounded-xl overflow-hidden border border-slate-200 mb-4" style={{ backgroundColor: colors.background }}>
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: colors.primary + '30' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: colors.primary }} />
              <span className="text-sm font-bold" style={{ color: colors.text }}>عنوان الموقع</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs cursor-pointer" style={{ color: colors.link }}>الرئيسية</span>
              <span className="text-xs cursor-pointer" style={{ color: colors.link }}>المصورين</span>
              <button
                className="text-xs px-3 py-1.5 rounded-lg text-white"
                style={{
                  background: `linear-gradient(to left, ${colors.heroCtaGradientStart}, ${colors.heroCtaGradientEnd})`,
                }}
              >
                حجز الآن
              </button>
            </div>
          </div>

          <div className="p-5">
            <h4 className="text-base font-bold mb-2" style={{ color: colors.text }}>
              نص رئيسي بلون النص الأساسي
            </h4>
            <p className="text-xs mb-3" style={{ color: colors.text, opacity: 0.65 }}>
              هذا نص فرعي يوضح كيف سيظهر المحتوى على الموقع بالألوان المختارة.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <button
                className="px-4 py-2 rounded-lg text-white text-xs font-medium transition-all"
                style={{ backgroundColor: colors.button }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.button}
              >
                زر أساسي (هوفر: تجربة)
              </button>
              <button
                className="px-4 py-2 rounded-lg text-xs font-medium border"
                style={{ borderColor: colors.secondary, color: colors.secondary }}
              >
                زر ثانوي
              </button>
              <button
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{ backgroundColor: 'transparent', color: colors.text }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = colors.accent; e.target.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = colors.text; }}
              >
                هوفر التمييز (Accent)
              </button>
              <a href="#" onClick={e => e.preventDefault()} className="text-xs underline" style={{ color: colors.link }}>
                رابط نصي
              </a>
            </div>
            {/* معاينة أيقونات القائمة */}
            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" style={{ backgroundColor: colors.background }}>
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Icon name="Menu" size={16} style={{ color: colors.menuIcon }} />
              </div>
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Icon name="Home" size={16} style={{ color: colors.menuIcon }} />
              </div>
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Icon name="Calendar" size={16} style={{ color: colors.menuIcon }} />
              </div>
              <span className="text-[10px] text-slate-400 mr-2">← لون أيقونات القائمة</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'أساسي', color: colors.primary },
            { label: 'ثانوي', color: colors.secondary },
            { label: 'أزرار', color: colors.button },
            { label: 'روابط', color: colors.link },
            { label: 'هوفر', color: colors.hover },
            { label: 'تمييز', color: colors.accent },
            { label: 'أيقونات القائمة', color: colors.menuIcon },
            { label: 'نص', color: colors.text },
            { label: 'فوتر', color: colors.footer },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5 bg-white rounded-lg px-2 py-1 border border-slate-100">
              <div className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: s.color }} />
              <span className="text-[10px] text-slate-600">{s.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 bg-white rounded-lg px-2 py-1 border border-slate-100">
            <div
              className="w-10 h-4 rounded border border-slate-200"
              style={{
                background: `linear-gradient(to left, ${colors.heroCtaGradientStart}, ${colors.heroCtaGradientEnd})`,
              }}
            />
            <span className="text-[10px] text-slate-600">تدرج زر الهيرو</span>
          </div>
        </div>
      </div>
    </div>
  );
}
